import React, {useCallback, useContext, useEffect, useRef, useState} from 'react'
import { useIntl } from 'react-intl'
import {ConnectorViewMappingContext} from './context/context'
import ConnectorMappingForm from './ConnectorMappingForm'
import ConnectorImage from './ConnectorImage'
import MappingSkeleton from './MappingSkeleton'
import {NavContext} from '../../../context/global_context'
import {useParams} from 'react-router-dom'
import {getRequest} from '../../../services/base'
import {getRandomString} from '../../../utils/common'
import {notifyError} from '../../../utils/notifications'

const ConnectorMapping = (props) => {
  const intl = useIntl();
  const { vehicle, connector_view } = useParams()
  const [state, dispatch] = useContext(ConnectorViewMappingContext)
  const [, navDispatch] = useContext(NavContext)
  const [loading, setLoading] = useState(true)
  const [isLocked, setIsLocked] = useState(false)
  const [containerHeight, setContainerHeight] = useState(null)
  const containerRef = useRef(null)
  const pollingTimerRef = useRef(null)

  const measureHeight = useCallback(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.clientHeight)
    }
  }, [])

  useEffect(() => {
    measureHeight()
    window.addEventListener('resize', measureHeight)
    return () => window.removeEventListener('resize', measureHeight)
  }, [measureHeight])

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({id: 'layout.adminTools'}),
        sub_header: `${intl.formatMessage({ id: 'layout.vehicleList' })} / ${vehicle} / ${intl.formatMessage({ id: 'sidebar.vehicleViews' })} / ${intl.formatMessage({ id: 'connectorViews.mapping.connectorMapping' })}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: vehicle,
        back_btn_url: `/vehicles/${vehicle}/connector_views/`,
        full_width_container: true,
      },
    })
    getConnectorViewData()
    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current)
      }
    }
  }, [])

  const getConnectorViewData = async () => {
    setLoading(true)

    try {
      let res = await getRequest(`/api/connector_views/${connector_view}/`)
      // base.js no longer rejects on a failed request, so the catch below only
      // sees genuine programming errors - the failure branch has to be explicit.
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.loadViewFailed'}))
        return
      }
      if (res?.is_locked) {
        setIsLocked(true)
        checkUploadStatus()
      } else {
        setIsLocked(false)
        dispatch({
          type: 'connector_view',
          data: { ...res, svg: `${res?.svg}?string=${getRandomString()}` },
        })
      }
    } catch (e) {
      notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.loadViewFailed'}))
    } finally {
      setLoading(false)
    }
  }

  const checkUploadStatus = async () => {
    try {
      let res = await getRequest(
        `/api/connector_views/upload_status/${connector_view}/`
      )
      if (res?.data === true && res?.status) {
        pollingTimerRef.current = setTimeout(() => {
          checkUploadStatus()
        }, 1500)
      } else {
        getConnectorViewData()
      }
    } catch (e) {
      notifyError(navDispatch, intl.formatMessage({id: 'connectorViews.mapping.checkUploadFailed'}))
    }
  }

  return (
    <div className="connector-mapping-page" ref={containerRef}>
      {loading ? (
        <div
          className="connector-mapping-container skeleton-layout"
          style={containerHeight ? { height: containerHeight } : undefined}
        >
          <MappingSkeleton />
        </div>
      ) : (
        <div
          className="connector-mapping-container"
          style={containerHeight ? { height: containerHeight } : undefined}
        >
          <ConnectorMappingForm />
          <ConnectorImage />
        </div>
      )}
    </div>
  )
}

export default ConnectorMapping
